import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AgentProfile {
  user_id: string
  full_name: string
  job_title?: string
  avatar_url?: string
  status: 'online' | 'away' | 'offline'
  status_message?: string
}

interface AgentProfileCardProps {
  agent: AgentProfile
  compact?: boolean
  showDetails?: boolean
  primaryColor?: string
  textColor?: string
  className?: string
  onStartChat?: (agentId: string) => void
}

export function AgentProfileCard({
  agent,
  compact = false,
  showDetails = true,
  primaryColor = '#3B82F6',
  textColor = '#1F2937',
  className,
  onStartChat,
}: AgentProfileCardProps) {
  const statusConfig = {
    online: {
      label: 'Available',
      color: 'bg-green-500',
      textColor: 'text-green-600',
      ringColor: 'ring-green-200',
    },
    away: {
      label: 'Away',
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      ringColor: 'ring-yellow-200',
    },
    offline: {
      label: 'Offline',
      color: 'bg-gray-400',
      textColor: 'text-gray-600',
      ringColor: 'ring-gray-200',
    },
  }

  const status = statusConfig[agent.status]
  const initials = agent.full_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors cursor-pointer',
          className
        )}
        onClick={() => onStartChat?.(agent.user_id)}>
        <div className="relative">
          <Avatar className="h-10 w-10">
            <AvatarImage src={agent.avatar_url} alt={agent.full_name} />
            <AvatarFallback className="text-sm font-medium" style={{ backgroundColor: primaryColor + '20', color: primaryColor }}>
              {initials}
            </AvatarFallback>
          </Avatar>
          <div
            className={cn(
              'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full',
              status.color
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate" style={{ color: textColor }}>
              {agent.full_name}
            </p>
            <span className={cn('text-xs', status.textColor)}>
              • {status.label}
            </span>
          </div>
          {agent.job_title && (
            <p className="text-xs text-gray-500 truncate">{agent.job_title}</p>
          )}
        </div>
        {agent.status === 'online' && onStartChat && (
          <MessageCircle
            className="h-4 w-4 text-gray-400 hover:text-gray-600"
            style={{ color: primaryColor }}
          />
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'p-4 rounded-lg border bg-white space-y-3',
        agent.status === 'online' && 'ring-2 ' + status.ringColor,
        className
      )}>
      {/* Header with Avatar and Status */}
      <div className="flex items-start gap-3">
        <div className="relative">
          <Avatar className="h-12 w-12">
            <AvatarImage src={agent.avatar_url} alt={agent.full_name} />
            <AvatarFallback style={{ backgroundColor: primaryColor + '20', color: primaryColor }}>
              {initials}
            </AvatarFallback>
          </Avatar>
          <div
            className={cn(
              'absolute -bottom-1 -right-1 h-4 w-4 rounded-full ring-2 ring-white',
              status.color
            )}
          />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm" style={{ color: textColor }}>
              {agent.full_name}
            </h4>
            <Badge
              variant="secondary"
              className={cn('text-xs px-2 py-0', status.textColor)}>
              {status.label}
            </Badge>
          </div>
          {agent.job_title && (
            <p className="text-xs text-gray-600 mt-0.5">{agent.job_title}</p>
          )}
          {agent.status_message && (
            <p className="text-xs text-gray-500 italic mt-1">"{agent.status_message}"</p>
          )}
        </div>
      </div>



      {/* Start Chat Button */}
      {agent.status === 'online' && onStartChat && (
        <button
          onClick={() => onStartChat(agent.user_id)}
          className="w-full py-2 px-3 rounded-md text-sm font-medium text-white transition-colors hover:opacity-90 flex items-center justify-center gap-2"
          style={{ backgroundColor: primaryColor }}>
          <MessageCircle className="h-4 w-4" />
          Chat with {agent.full_name.split(' ')[0]}
        </button>
      )}

    </div>
  )
}